import { FetchXmlError } from "./errors.js";
import { ELEMENT_RULES, type FetchElement } from "./model.js";
import { DATAVERSE_OPERATOR_MAP, VALUEOF_COMPARISON_OPERATORS } from "./operators.js";
const AGGREGATE_FUNCTIONS = new Set(["count", "countcolumn", "sum", "avg", "min", "max"]);
const LINK_TYPES = new Set([
  "inner",
  "outer",
  "any",
  "not any",
  "all",
  "not all",
  "exists",
  "in",
  "matchfirstrowusingcrossapply",
]);

function asBoolean(value: string | number | boolean | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const lowered = value.toLowerCase();
  if (lowered === "true" || lowered === "1") return true;
  if (lowered === "false" || lowered === "0") return false;
  return undefined;
}

function gatherElements(node: FetchElement, name: FetchElement["name"], output: FetchElement[] = []): FetchElement[] {
  if (node.name === name) output.push(node);
  node.children.forEach((child) => gatherElements(child, name, output));
  return output;
}

function getConditionValueCount(node: FetchElement): number {
  if (node.name !== "condition") return 0;

  const childValues = node.children.filter((child) => child.name === "value").length;
  if (childValues > 0) return childValues;

  const valuesAttribute = node.attributes.values;
  if (typeof valuesAttribute === "string") {
    return valuesAttribute
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0).length;
  }

  if (valuesAttribute !== undefined) {
    return 1;
  }

  if ("value" in node.attributes) return 1;
  return 0;
}

function validateAttributeNode(node: FetchElement, path: string): void {
  if (node.name !== "attribute") {
    return;
  }

  const aggregate = node.attributes.aggregate;
  const groupBy = asBoolean(node.attributes.groupby) === true;

  if (aggregate !== undefined) {
    const aggregateName = String(aggregate).toLowerCase();
    if (!AGGREGATE_FUNCTIONS.has(aggregateName)) {
      throw new FetchXmlError(
        "invalid-attribute-value",
        `Unsupported aggregate function '${String(aggregate)}'`,
        path,
      );
    }
  }

  if (aggregate !== undefined && groupBy) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      "Attribute cannot set both aggregate and groupby='true'",
      path,
    );
  }
}

function validateLinkEntityNode(node: FetchElement, path: string): void {
  if (node.name !== "link-entity") {
    return;
  }

  const from = node.attributes.from;
  const to = node.attributes.to;
  if (String(from).trim().length === 0 || String(to).trim().length === 0) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      "link-entity attributes 'from' and 'to' must be non-empty",
      path,
    );
  }

  const linkType = node.attributes["link-type"];
  if (linkType !== undefined && !LINK_TYPES.has(String(linkType).toLowerCase())) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      `Unsupported link-type '${String(linkType)}'`,
      path,
    );
  }
}

function validateCondition(node: FetchElement, path: string): void {
  if (node.name !== "condition") {
    return;
  }

  const operator = node.attributes.operator;
  const operatorName = typeof operator === "string" ? operator.toLowerCase() : "";
  const hasValuesAttribute = "values" in node.attributes;
  const hasValueAttribute = "value" in node.attributes;
  const hasValueOfAttribute = "valueof" in node.attributes;
  const valueChildrenCount = node.children.filter((child) => child.name === "value").length;
  const valueCount = getConditionValueCount(node);

  const operatorSpec = DATAVERSE_OPERATOR_MAP.get(operatorName);
  if (!operatorSpec) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      `Unsupported condition operator '${String(operator)}'`,
      path,
    );
  }

  if (hasValueAttribute && valueChildrenCount > 0) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      "Condition cannot mix 'value' attribute with <value> children",
      path,
    );
  }

  if (hasValueOfAttribute) {
    if (!VALUEOF_COMPARISON_OPERATORS.has(operatorName)) {
      throw new FetchXmlError(
        "invalid-attribute-value",
        `Operator '${operatorName}' does not support 'valueof' comparisons`,
        path,
      );
    }
    if (hasValuesAttribute || hasValueAttribute || valueChildrenCount > 0) {
      throw new FetchXmlError(
        "invalid-attribute-value",
        "Condition using 'valueof' cannot include value payload",
        path,
      );
    }
    return;
  }

  switch (operatorSpec.arity) {
    case "none": {
      if (valueCount > 0) {
        throw new FetchXmlError(
          "invalid-attribute-value",
          `Operator '${operatorName}' cannot include value payload`,
          path,
        );
      }
      break;
    }
    case "single": {
      if (valueCount !== 1) {
        throw new FetchXmlError(
          "invalid-attribute-value",
          `Operator '${operatorName}' requires exactly 1 value`,
          path,
        );
      }
      break;
    }
    case "multiple": {
      if (valueCount < 1) {
        throw new FetchXmlError(
          "invalid-attribute-value",
          `Operator '${operatorName}' requires one or more values`,
          path,
        );
      }
      break;
    }
    case "exactly-two": {
      if (valueCount !== 2) {
        throw new FetchXmlError(
          "invalid-attribute-value",
          `Operator '${operatorName}' requires exactly 2 values`,
          path,
        );
      }
      break;
    }
    default: {
      throw new FetchXmlError(
        "invalid-attribute-value",
        `Unhandled operator arity for '${operatorName}'`,
        path,
      );
    }
  }
}

function validateAggregateQuery(node: FetchElement): void {
  if (node.name !== "fetch") {
    return;
  }

  const aggregateEnabled = asBoolean(node.attributes.aggregate) === true;
  const attributeNodes = gatherElements(node, "attribute");
  const aggregateOrGroupByNodes = attributeNodes.filter((attributeNode) => {
    const hasAggregate = "aggregate" in attributeNode.attributes;
    const groupBy = asBoolean(attributeNode.attributes.groupby) === true;
    return hasAggregate || groupBy;
  });

  if (!aggregateEnabled && aggregateOrGroupByNodes.length > 0) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      "Fetch root must set aggregate='true' when aggregate/groupby attributes are used",
      "fetch",
    );
  }

  if (!aggregateEnabled) {
    return;
  }

  if (attributeNodes.length === 0) {
    throw new FetchXmlError(
      "invalid-attribute-value",
      "Aggregate fetch query requires at least one <attribute>",
      "fetch",
    );
  }

  attributeNodes.forEach((attributeNode, index) => {
    const hasAggregate = "aggregate" in attributeNode.attributes;
    const groupBy = asBoolean(attributeNode.attributes.groupby) === true;
    const alias = attributeNode.attributes.alias;

    if (!hasAggregate && !groupBy) {
      throw new FetchXmlError(
        "invalid-attribute-value",
        "In aggregate query, each <attribute> must declare aggregate or groupby='true'",
        `fetch/attribute[${index}]`,
      );
    }

    if ((hasAggregate || groupBy) && (alias === undefined || String(alias).trim().length === 0)) {
      throw new FetchXmlError(
        "invalid-attribute-value",
        "Aggregate/groupby attribute requires non-empty alias",
        `fetch/attribute[${index}]`,
      );
    }
  });
}

function assertKnownElement(node: FetchElement, path: string): void {
  if (!Object.prototype.hasOwnProperty.call(ELEMENT_RULES, node.name)) {
    throw new FetchXmlError("unknown-element", `Unknown element '${node.name}'`, path);
  }
}

export function validateFetchElement(node: FetchElement, path = "fetch"): void {
  assertKnownElement(node, path);
  const rule = ELEMENT_RULES[node.name];

  for (const required of rule.requiredAttributes) {
    if (!(required in node.attributes)) {
      throw new FetchXmlError(
        "missing-required-attribute",
        `Element '${node.name}' is missing required attribute '${required}'`,
        path,
      );
    }
  }

  for (const key of Object.keys(node.attributes)) {
    if (!rule.allowedAttributes.includes(key)) {
      throw new FetchXmlError(
        "unknown-attribute",
        `Unknown attribute '${key}' on element '${node.name}'`,
        path,
      );
    }
  }

  validateCondition(node, path);
  validateAttributeNode(node, path);
  validateLinkEntityNode(node, path);

  node.children.forEach((child, index) => {
    if (!rule.allowedChildren.includes(child.name)) {
      throw new FetchXmlError(
        "invalid-child",
        `Element '${child.name}' cannot be child of '${node.name}'`,
        `${path}/${child.name}[${index}]`,
      );
    }
    validateFetchElement(child, `${path}/${child.name}[${index}]`);
  });
}

export function validateRoot(node: FetchElement): void {
  if (node.name !== "fetch") {
    throw new FetchXmlError("invalid-root", "Root element must be 'fetch'", "root");
  }
  validateFetchElement(node);
  validateAggregateQuery(node);
}
